/**
 * 上传页：按组合框结果解析目标 Project（文件夹）ID。
 * - 已点选下拉项 → 直接使用锁定的 project_id
 * - 未点选直接提交 → resolveProjectTitle(intent=create)；撞名时询问加入 vs 强制新建
 */
import { Modal } from 'ant-design-vue'
import i18n from '@/i18n'
import {
  resolveProjectTitle,
  type ProjectResolveTitleResponse,
} from '@/modules/project/api/project'

function t(key: string, params?: Record<string, unknown>): string {
  return String(i18n.global.t(key, params ?? {}))
}

function requireProjectId(
  result: ProjectResolveTitleResponse,
  fallback?: number | null,
): number {
  const id = result.project_id ?? fallback
  if (id == null) {
    throw new Error(t('project.titleCombo.resolveFailed'))
  }
  return id
}

function askDuplicateChoice(
  existingProjectId: number,
  suggestedTitle: string | null | undefined,
): Promise<'join' | 'force_create'> {
  return new Promise((resolve) => {
    Modal.confirm({
      title: t('project.titleCombo.duplicateTitle'),
      content: t('project.titleCombo.duplicateContent', {
        suggested: suggestedTitle || '',
      }),
      okText: t('project.titleCombo.joinExisting'),
      cancelText: t('project.titleCombo.forceCreate'),
      maskClosable: false,
      keyboard: false,
      onOk: () => {
        resolve('join')
        return existingProjectId
      },
      onCancel: () => {
        resolve('force_create')
      },
    })
  })
}

export interface ResolveProjectForUploadOptions {
  title: string
  /** 下拉点选锁定的项目 ID；有值则跳过 resolve */
  lockedProjectId?: number | null
}

/**
 * 解析上传目标文件夹 project_id。
 * 可能弹窗询问撞名处理；取消弹窗时 reject。
 * 已锁定 project_id 时跳过标题校验（从项目详情带入等）。
 */
export async function resolveProjectForUpload(
  options: ResolveProjectForUploadOptions,
): Promise<number> {
  if (options.lockedProjectId != null) {
    return options.lockedProjectId
  }

  const title = options.title.trim()
  if (!title) {
    throw new Error(t('project.titleCombo.titleRequired'))
  }

  const result = await resolveProjectTitle({
    title,
    intent: 'create',
  })

  if (result.action === 'create' || result.action === 'join') {
    return requireProjectId(result)
  }

  // duplicate_prompt
  const existingId = result.existing_project_id
  if (existingId == null) {
    throw new Error(t('project.titleCombo.resolveFailed'))
  }

  const choice = await askDuplicateChoice(existingId, result.suggested_title)
  if (choice === 'join') {
    return existingId
  }

  const forced = await resolveProjectTitle({
    title,
    force_create: true,
  })
  return requireProjectId(forced)
}
